import { useAppStore } from "@/store";
import { TopicInfo } from "@/design-system/components/item-list/types";
import styled from "@emotion/styled";
import { CollectionIcon } from "@/components/collection/CollectionIcon";
import { MdsIconKind } from "@/design-system/components/icon";
import { removeNoteFromCollection } from "@/actions/removeNoteFromCollection";
import { observer } from "mobx-react-lite";
import { INoteObservable, NotesIndexTuple } from "@/store/note";
import { FigmaMenuItem } from "@/mds/menu-item";
import { MDSMenu } from "@/mds/menu";
import { MDSText, MDSTextColor, MDSTextOverflow, MDSTextVariant, MDSTextWeight } from "@/mds";
import { ReactEventHandler } from "react";
import { MDSButton, MDSButtonSlotName, MDSButtonIcon } from "@/mds";
import { useMaybeTabContext } from "@/components/layout/TabContext";
import { useLiveQuery } from "dexie-react-hooks";
import { LensKind, SortByKind } from "@/modules/lenses/types";
import { useThemedCollectionColors } from "@/mds/themes/useThemedCollectionColors";

interface CollectionPreviewProps {
  collectionTopic: TopicInfo;
  noteId: string;
  onDismiss: () => void;
}
export const CollectionPreview = observer(
  ({ collectionTopic, noteId, onDismiss }: CollectionPreviewProps) => {
    const { store } = useAppStore();
    const tabContext = useMaybeTabContext();

    const { bgColor } = useThemedCollectionColors(collectionTopic.id);
    const collection = store.collections.getCollectionObservableById({
      collectionId: collectionTopic.id,
    });
    const note = store.notes.getNoteObservableById({ noteId });

    const recentNoteResults = useLiveQuery(() =>
      store.notes.search({
        lens: LensKind.All,
        sortBy: SortByKind.LastInteracted,
        limit: 5,
        filter: { collections: [{ ids: [collectionTopic.id], mode: "all" }] },
      })
    );
    const recentNotes = store.notes.getMany(
      recentNoteResults?.map((result: NotesIndexTuple) => result.at(-1) as string) ?? []
    );

    const handleRemove: React.MouseEventHandler = e => {
      if (!note || !collection) return;

      e.stopPropagation();

      removeNoteFromCollection({
        note,
        collection: collection,
        store: store,
      });

      return true;
    };

    const handleOpenInSplit: ReactEventHandler = e => {
      if (!collection) return;

      store.navigation.goToCollection({
        collectionId: collection.id,
        openInInactivePane: true,
        activeTabId: tabContext?.activeTab.id,
      });

      e.stopPropagation();
      return true;
    };

    const handleOpenNote = (note: INoteObservable) => {
      if (!note) return;

      onDismiss();
      store.navigation.goToNote({ noteId: note.id });
    };

    if (!collection) return null;

    return (
      <CollectionPreviewContainer>
        <Header style={{ backgroundColor: bgColor }}>
          <div>
            <CollectionIcon collectionId={collection.id} />
          </div>
          <HeaderTitle>
            <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
              {collection.label}
            </MDSText>
            <MDSText
              variant={MDSTextVariant.TextXs}
              color={MDSTextColor.Secondary}
              overflow={MDSTextOverflow.Ellipsis}
            >
              {collection.description}
            </MDSText>
          </HeaderTitle>
        </Header>
        <Main>
          <RecentNotes notes={recentNotes} onOpenNote={handleOpenNote} />
          <CardActions>
            {noteId && (
              <MDSButton onClick={handleRemove}>
                <MDSButtonIcon slot={MDSButtonSlotName.LeftIcon} kind={MdsIconKind.Exit} />
                Remove
              </MDSButton>
            )}
            <MDSButton onClick={handleOpenInSplit}>
              <MDSButtonIcon slot={MDSButtonSlotName.LeftIcon} kind={MdsIconKind.SplitPanel} />
              Open in split
            </MDSButton>
          </CardActions>
        </Main>
      </CollectionPreviewContainer>
    );
  }
);

interface RecentNotesProps {
  notes: INoteObservable[];
  onOpenNote: (note: INoteObservable) => void;
}
const RecentNotes = ({ notes, onOpenNote }: RecentNotesProps) => (
  <Recents>
    <MDSText
      variant={MDSTextVariant.TextXs}
      weight={MDSTextWeight.SemiBold}
      color={MDSTextColor.Secondary}
    >
      Recents
    </MDSText>
    <RecentsItems>
      <MDSMenu>
        {notes.map(
          note =>
            note && (
              <FigmaMenuItem
                key={note.id}
                type="note"
                text={note.title}
                onAction={() => {
                  onOpenNote(note);
                }}
              />
            )
        )}
      </MDSMenu>
    </RecentsItems>
  </Recents>
);

const CollectionPreviewContainer = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.colors.surface.preview,
  minWidth: 290,
  maxWidth: 380,
  borderRadius: theme.borderRadius.large,
  overflow: "hidden",
  boxShadow: theme.shadows.surface,
}));

const Header = styled.div(({ theme }) => ({
  padding: `${theme.spacing.smd} ${theme.spacing.md}`,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing.sm,
}));

const HeaderTitle = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.xxs,
}));

const Main = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.sm,
  margin: 0,
  padding: 0,
  paddingTop: theme.spacing.sm,

  borderBottomLeftRadius: theme.borderRadius.large,
  borderBottomRightRadius: theme.borderRadius.large,

  borderLeft: `1px solid ${theme.colors.border.surface}`,
  borderRight: `1px solid ${theme.colors.border.surface}`,
  borderBottom: `1px solid ${theme.colors.border.surface}`,
  borderTop: "none",
}));

const Recents = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.xxs,
  padding: `0 ${theme.spacing.md}`,
}));

const RecentsItems = styled.div(({ theme }) => ({
  marginLeft: `-${theme.spacing.sm}`,
  marginRight: `-${theme.spacing.sm}`,
}));

const CardActions = styled.div(({ theme }) => ({
  display: "flex",
  gap: theme.spacing.sm,
  padding: `0 ${theme.spacing.md} ${theme.spacing.smd} ${theme.spacing.md}`,
}));
